/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { StrictHttpResponse } from '../strict-http-response';
import { WeatherApiConfiguration } from '../weather-api-configuration';

import { CreateJob$Params, createJob } from '../fn/job/create-job';
import { ExecuteJobs$Params, executeJobs } from '../fn/job/execute-jobs';
import { GetJobCrons$Params, getJobCrons } from '../fn/job/get-job-crons';
import { GetJobs$Params, getJobs } from '../fn/job/get-jobs';
import { SaveCron$Params, saveCron } from '../fn/job/save-cron';
import { UpdateCron$Params, updateCron } from '../fn/job/update-cron';
import { UpdateJobTime$Params, updateJobTime } from '../fn/job/update-job-time';
import { Job } from '../models/job';
import { JobCrons } from '../models/job-crons';

/**
 * Job API
 */
@Injectable({ providedIn: 'root' })
export class JobService extends BaseService {
   constructor(config: WeatherApiConfiguration, http: HttpClient) {
      super(config, http);
   }

   /** Path part for operation `getJobs()` */
   static readonly GetJobsPath = '/api/weather/job';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `getJobs()` instead.
    *
    * This method doesn't expect any request body.
    */
   getJobs$Response(params?: GetJobs$Params, context?: HttpContext): Observable<StrictHttpResponse<Job>> {
      return getJobs(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `getJobs$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   getJobs(params?: GetJobs$Params, context?: HttpContext): Observable<Job> {
      return this.getJobs$Response(params, context).pipe(map((r: StrictHttpResponse<Job>): Job => r.body));
   }

   /** Path part for operation `createJob()` */
   static readonly CreateJobPath = '/api/weather/job';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `createJob()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   createJob$Response(params: CreateJob$Params, context?: HttpContext): Observable<StrictHttpResponse<Job>> {
      return createJob(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `createJob$Response()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   createJob(params: CreateJob$Params, context?: HttpContext): Observable<Job> {
      return this.createJob$Response(params, context).pipe(map((r: StrictHttpResponse<Job>): Job => r.body));
   }

   /** Path part for operation `getJobCrons()` */
   static readonly GetJobCronsPath = '/api/weather/job/cron';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `getJobCrons()` instead.
    *
    * This method doesn't expect any request body.
    */
   getJobCrons$Response(params?: GetJobCrons$Params, context?: HttpContext): Observable<StrictHttpResponse<JobCrons>> {
      return getJobCrons(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `getJobCrons$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   getJobCrons(params?: GetJobCrons$Params, context?: HttpContext): Observable<JobCrons> {
      return this.getJobCrons$Response(params, context).pipe(
         map((r: StrictHttpResponse<JobCrons>): JobCrons => r.body)
      );
   }

   /** Path part for operation `saveCron()` */
   static readonly SaveCronPath = '/api/weather/job/cron';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `saveCron()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   saveCron$Response(params: SaveCron$Params, context?: HttpContext): Observable<StrictHttpResponse<JobCrons>> {
      return saveCron(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `saveCron$Response()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   saveCron(params: SaveCron$Params, context?: HttpContext): Observable<JobCrons> {
      return this.saveCron$Response(params, context).pipe(map((r: StrictHttpResponse<JobCrons>): JobCrons => r.body));
   }

   /** Path part for operation `updateCron()` */
   static readonly UpdateCronPath = '/api/weather/job/cron/replacement';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `updateCron()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   updateCron$Response(params: UpdateCron$Params, context?: HttpContext): Observable<StrictHttpResponse<JobCrons>> {
      return updateCron(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `updateCron$Response()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   updateCron(params: UpdateCron$Params, context?: HttpContext): Observable<JobCrons> {
      return this.updateCron$Response(params, context).pipe(map((r: StrictHttpResponse<JobCrons>): JobCrons => r.body));
   }

   /** Path part for operation `executeJobs()` */
   static readonly ExecuteJobsPath = '/api/weather/job/execution/{providerId}';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `executeJobs()` instead.
    *
    * This method doesn't expect any request body.
    */
   executeJobs$Response(params: ExecuteJobs$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
      return executeJobs(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `executeJobs$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   executeJobs(params: ExecuteJobs$Params, context?: HttpContext): Observable<void> {
      return this.executeJobs$Response(params, context).pipe(map((r: StrictHttpResponse<void>): void => r.body));
   }

   /** Path part for operation `updateJobTime()` */
   static readonly UpdateJobTimePath = '/api/weather/job/time';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `updateJobTime()` instead.
    *
    * This method doesn't expect any request body.
    */
   updateJobTime$Response(params: UpdateJobTime$Params, context?: HttpContext): Observable<StrictHttpResponse<Job>> {
      return updateJobTime(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `updateJobTime$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   updateJobTime(params: UpdateJobTime$Params, context?: HttpContext): Observable<Job> {
      return this.updateJobTime$Response(params, context).pipe(map((r: StrictHttpResponse<Job>): Job => r.body));
   }
}
